/**
 * Component: EmployeeSelectionList
 * @description Displays a list of employees filtered by the specified role, allowing selection for a shift assignment.
 *
 * @props
 *   @param {function} onSelect - Callback function triggered when an employee is selected.
 *   @param {string} shiftType - The shift type to filter employees by.
 *   @param {Object} shiftsByAssignment - Map of shifts by employee assignment.
 *
 * @redux
 *   @action fetchEmployees - Dispatches an action to fetch employees from Firestore.
 *   @selector employees - Retrieves the list of employees from the Redux store.
 *   @selector selectSelectedShift - Retrieves the most up-to-date shift data from Redux store.
 *
 * @state
 *   - Filters employees based on their `shiftTypes` matching the provided role.
 *
 * @render
 *   - Displays a list of employees with avatars, names, and roles.
 *   - Calls the `onSelect` function with the selected employee when clicked.
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/WarningAmber';

import ConflictsDialog from '../ShiftInformation/ConflictsDialog';
import SearchBar from '../SearchBar/SearchBar';
import { fetchEmployees } from '../../redux/actions/employeesActions';
import { fetchAvailability } from '../../redux/actions/shiftActions';
import { hasOverlappingShifts } from '../../utils/shiftUtils';
import {
  HeaderText,
  EmployeeButton,
  EmployeeInfo,
  EmployeeDetails,
  RoleText,
  StyledBackButton,
  HeaderContainer,
  EmployeeListContainer,
  EmployeeDisplayContainer,
  SearchContainer,
} from './EmployeeSelectionList.styles';
import { StyledAvatar } from '../StyledAvatar/StyledAvatar';
import { selectAllEmployees } from '../../redux/slices/employeesSlice';
import { selectShiftAvailability } from '../../redux/slices/shiftSlice';
import { selectSelectedShift } from '../../redux/shifts/shiftsSlice';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';
import { validateShift } from '../../redux/shifts/shiftsService';

const EmployeeSelectionList = ({ onSelect, shiftType, shiftTime, shiftsByAssignment, onBack, onUnassign }) => {
  const dispatch = useDispatch();
  const employees = useSelector(selectAllEmployees);
  // Get the most up-to-date shift data from Redux
  const shiftFromStorage = useSelector(selectSelectedShift);
  const shiftAvailability = useSelector(selectShiftAvailability);
  const currentVenue = useSelector(selectCurrentVenue);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  useEffect(() => {
    if (isFetching) return;
    setIsFetching(true);
    dispatch(fetchAvailability(employees, shiftFromStorage, currentVenue?.id));
  }, [dispatch, employees, isFetching, shiftFromStorage, currentVenue?.id]);

  // Filter employees based whether they're active, whether they can work the shift type, and whether they have availability
  const filteredEmployees = employees
    .filter((employee) => {
      const matchesSearch = !searchQuery || employee.displayName?.toLowerCase().includes(searchQuery.toLowerCase());
      return (
        employee.isActive && !hasOverlappingShifts(employee.id, shiftFromStorage, shiftsByAssignment) && matchesSearch
      );
    })
    .sort((a, b) => b.shiftTypes?.includes(shiftType) - a.shiftTypes?.includes(shiftType));

  const handleConflicts = (conflicts, employeeInfo) => {
    setSelectedEmployee(employeeInfo);
    setConflicts(
      conflicts.concat(
        !employeeInfo.shiftTypes?.includes(shiftType)
          ? [
              {
                type: 'selected_role',
                caption: 'Selected role:',
                description: shiftType,
              },
              {
                type: 'available_roles',
                caption: !employeeInfo.shiftTypes?.length ? 'Employee has no roles' : 'Available roles:',
                description: [].concat(employeeInfo.shiftTypes || []).join(', '),
              },
            ]
          : []
      )
    );
  };

  const hasConflicts = (employee) =>
    !employee.shiftTypes?.includes(shiftType) || shiftAvailability?.[employee.id]?.length > 0;

  const validateSelectedEmployee = async (employeeInfo, invalidType) => {
    setLoading(employeeInfo.id);

    const [start, end] = shiftTime;
    const conflicts = await validateShift(start, end, employeeInfo.id, currentVenue?.id);

    if (invalidType || conflicts.length > 0) {
      handleConflicts(conflicts, employeeInfo);
    } else {
      onSelect(employeeInfo);
    }

    setLoading(null);
  };

  const handleEmployeeClick = (employee) => {
    if (loading) return;
    if (onSelect) {
      const invalidType = !employee.shiftTypes?.includes(shiftType);
      if (shiftAvailability?.[employee.id]?.length > 0) {
        handleConflicts(shiftAvailability[employee.id], employee);
      } else if (!shiftAvailability?.[employee.id]) {
        validateSelectedEmployee(employee, invalidType);
      } else if (invalidType) {
        handleConflicts([], employee);
      } else {
        onSelect(employee);
      }
    }
  };

  const handleUnassignClick = () => {
    if (onUnassign) {
      onUnassign();
    }
  };

  const handleClose = () => {
    setConflicts([]);
    setSelectedEmployee({});
  };

  const handleAssign = () => {
    onSelect(selectedEmployee, conflicts);
    handleClose();
  };

  return (
    <Box>
      <ConflictsDialog
        conflicts={conflicts}
        handleClose={handleClose}
        handleAssign={handleAssign}
        selectedEmployee={selectedEmployee}
      />

      {/* Header with Back Button */}
      <HeaderContainer>
        {onBack && (
          <StyledBackButton onClick={onBack} aria-label="Back">
            <ArrowBackIcon />
          </StyledBackButton>
        )}
        <HeaderText variant="copy">AVAILABLE FOR SHIFT</HeaderText>
      </HeaderContainer>

      {/* Search Bar */}
      <SearchContainer>
        <SearchBar searchTerm={searchQuery} setSearchTerm={setSearchQuery} placeholder="Search employees..." />
      </SearchContainer>

      <EmployeeListContainer>
        {/* Unassigned Option */}
        {onUnassign && (
          <EmployeeButton onClick={handleUnassignClick}>
            <EmployeeInfo>
              <StyledAvatar size="small" alt="Unassigned" />
              <EmployeeDetails>
                <Typography variant="h6">Unassigned</Typography>
                <RoleText variant="body2">Remove current assignment</RoleText>
              </EmployeeDetails>
            </EmployeeInfo>
          </EmployeeButton>
        )}

        {filteredEmployees.map((employee) => (
          <EmployeeButton
            key={employee.id}
            onClick={() => handleEmployeeClick(employee)}
            disabled={loading === employee.id}
          >
            <EmployeeInfo>
              {loading === employee.id ? (
                <CircularProgress color="inherit" size="36px" />
              ) : (
                <StyledAvatar size="small" alt={employee.displayName} src={employee.profileImageUrl} />
              )}
              <EmployeeDetails>
                <EmployeeDisplayContainer>
                  <Typography variant="h6">{employee.displayName}</Typography>
                  {hasConflicts(employee) && <WarningIcon />}
                </EmployeeDisplayContainer>
                {employee.shiftTypes && employee.shiftTypes.length > 0 && (
                  <RoleText variant="body2">{employee.shiftTypes.join(', ')}</RoleText>
                )}
              </EmployeeDetails>
            </EmployeeInfo>
          </EmployeeButton>
        ))}
      </EmployeeListContainer>
    </Box>
  );
};

export default EmployeeSelectionList;
